import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Col, Container, Row } from "reactstrap"
import { Card } from "primereact/card"
import { Calendar } from "primereact/calendar"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { Dropdown } from "primereact/dropdown"

import {
  CalendarCheck,
  CheckCircle2,
  Clock3,
  FileText,
  Info,
  UsersRound,
  XCircle,
} from "lucide-react"
import axios from "axios"
import {
  PieChart,
  Pie,
  Cell,
  // Tooltip,
} from "recharts"
import {
  Chart,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  DoughnutController,
} from "chart.js"

// Register required components
Chart.register(ArcElement, ChartTooltip, Legend, DoughnutController)
import { triggerAlert } from "utils/CommonFunctions"
import { mapAtsUsersToOptions } from "utils/userOptions"
import OrgTableShimmer from "ATSComponents/Common/OrgTableShimmer"
import "./ManagerDashboard.css"
import { Dialog } from "primereact/dialog"
import CompanySelector from "ATSComponents/Common/CompanySelector"

const rangeOptions = [
  { name: "Custom Range", keyID: "custom" },
  { name: "This Week", keyID: "this_week" },
  { name: "Last 30 Days", keyID: "30_days" },
  { name: "One Year", keyID: "one_year" },
]

const safeArray = value => (Array.isArray(value) ? value : [])

const toDate = value => {
  if (!value) return null
  if (value instanceof Date) return value

  const dateText = String(value).trim()
  const displayDateMatch = dateText.match(
    /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})$/
  )

  if (displayDateMatch) {
    const monthIndex = {
      jan: 0,
      feb: 1,
      mar: 2,
      apr: 3,
      may: 4,
      jun: 5,
      jul: 6,
      aug: 7,
      sep: 8,
      oct: 9,
      nov: 10,
      dec: 11,
    }[displayDateMatch[2].slice(0, 3).toLowerCase()]

    if (monthIndex !== undefined) {
      return new Date(
        Number(displayDateMatch[3]),
        monthIndex,
        Number(displayDateMatch[1])
      )
    }
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const normalizeLabel = value => {
  const text = (value ?? "").toString().trim()
  return text || "-"
}

const formatDate = value => {
  const parsed = toDate(value)
  if (!parsed) return value ? normalizeLabel(value) : "-"
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}
// ─── Palette for pipeline stages ───────────────────────────────────────────
const STAGE_COLORS = {
  Received: "#6366f1",
  Interview: "#3b82f6",
  "Panel Round": "#0ea5e9",
  Shortlisted: "#10b981",
  Offered: "#f59e0b",
  Documentation: "#8b5cf6",
  "Task Assessment": "#ec4899",
  Hold: "#94a3b8",
  Responded: "#14b8a6",
  "Not Responded": "#f87171",
  "Screening Round": "#fb923c",
  "L1 Round": "#a78bfa",
  "L2 Round": "#34d399",
  Placed: "#22c55e",
  Rejected: "#ef4444",
}
const DEFAULT_COLOR = "#cbd5e1"
const PipelineChart = ({
  stageSummary,
  onStageClick,
  selectedPipelineStage,
}) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [hoveredStage, setHoveredStage] = useState(null)

  const chartData = stageSummary.filter(s => s.count > 0) // for donut
  const allStages = stageSummary // for cards

  const total = allStages.reduce((sum, s) => sum + s.count, 0)
  const sorted = [...allStages].sort((a, b) => b.count - a.count)

  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return
    if (chartInstance.current) chartInstance.current.destroy()

    chartInstance.current = new Chart(chartRef.current, {
      type: "doughnut",
      data: {
        labels: chartData.map(d => d.stage),
        datasets: [
          {
            data: chartData.map(d => d.count),
            backgroundColor: chartData.map(
              d => STAGE_COLORS[d.stage] || DEFAULT_COLOR
            ),
            borderWidth: 2,
            borderColor: "#ffffff",
            hoverOffset: 0,
            hoverBorderWidth: 3,
          },
        ],
      },
      options: {
        responsive: false,
        cutout: "70%",
        animation: { animateRotate: true, animateScale: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: ctx => ` ${ctx.label}: ${ctx.parsed.toLocaleString()}`,
            },
            bodyFont: { size: 12 },
            padding: 8,
            cornerRadius: 6,
          },
        },
        onHover: (e, els) => {
          setHoveredStage(els.length ? chartData[els[0].index] : null)
        },
      },
    })
    return () => chartInstance.current?.destroy()
  }, [chartData])

  if (allStages.length === 0)
    return (
      <div
        style={{
          textAlign: "center",
          padding: "32px 0",
          color: "#94a3b8",
          fontSize: "13px",
        }}
      >
        No pipeline data available.
      </div>
    )

  const displayVal = hoveredStage ? hoveredStage.count : total
  const displayLabel = hoveredStage ? hoveredStage.stage : "Total"
  const displayColor = hoveredStage
    ? STAGE_COLORS[hoveredStage.stage] || DEFAULT_COLOR
    : "#1e293b"
  const maxCount = sorted[0]?.count || 1

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "14px",
        height: "100%",
      }}
    >
      {/* Top row: Donut + Summary */}
      <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
        {/* Donut */}
        <div style={{ position: "relative", flexShrink: 0 }}>
          <canvas ref={chartRef} width={110} height={110} />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                fontSize: "15px",
                fontWeight: 700,
                color: displayColor,
                lineHeight: 1,
                transition: "color .15s",
              }}
            >
              {displayVal.toLocaleString()}
            </div>
            <div
              style={{
                fontSize: "8px",
                color: "#94a3b8",
                marginTop: "2px",
                maxWidth: "48px",
                lineHeight: 1.2,
              }}
            >
              {displayLabel}
            </div>
          </div>
        </div>

        {/* Total + top pills */}
        {/* Total + top pills */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "8px",
              fontWeight: 500,
            }}
          >
            {total.toLocaleString()} candidates across {allStages.length} stages
          </div>
          {/* Pills — all stages, wrap into rows */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
            }}
          >
            {sorted.map(d => {
              {
                /* ← was sorted.slice(0, 4) */
              }
              const color = STAGE_COLORS[d.stage] || DEFAULT_COLOR
              const isSelected = selectedPipelineStage === d.stage
              return (
                <button
                  key={d.stage}
                  type="button"
                  onClick={() => onStageClick(d.stage)}
                  onMouseEnter={() => setHoveredStage(d)}
                  onMouseLeave={() => setHoveredStage(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    padding: "5px 10px",
                    borderRadius: "8px",
                    background: isSelected ? `${color}15` : `${color}08`,
                    border: `1px solid ${color}30`,
                    cursor: "pointer",
                    transition: "all .12s",
                    minWidth: "120px", // ← ensures they don't get too squished
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        background: color,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#475569",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {d.stage}
                    </span>
                  </div>
                  {/* <strong style={{ fontSize: "13px", color, flexShrink: 0 }}>
                    {d.count.toLocaleString()}
                  </strong> */}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ borderTop: "1px solid #f1f5f9" }} />

      {/* Stage cards — 4 per row, fill full width */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "8px",

          maxHeight: "260px", // 👈 control height here
          overflowY: "auto", // 👈 enables vertical scroll
          paddingRight: "4px", // 👈 avoids scrollbar overlap
        }}
      >
        {sorted.map(d => {
          const color = STAGE_COLORS[d.stage] || DEFAULT_COLOR
          const isSelected = selectedPipelineStage === d.stage
          const isHovered = hoveredStage?.stage === d.stage
          const barPct = Math.round(
            (Math.log(d.count + 1) / Math.log(maxCount + 1)) * 100
          )
          const pct = Math.round((d.count / total) * 100)

          return (
            <button
              key={d.stage}
              type="button"
              onClick={() => onStageClick(d.stage)}
              onMouseEnter={() => setHoveredStage(d)}
              onMouseLeave={() => setHoveredStage(null)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "8px",
                padding: "14px 14px 10px",
                borderRadius: "10px",
                border: isSelected
                  ? `1.5px solid ${color}`
                  : isHovered
                  ? `1px solid ${color}80`
                  : "1px solid #eef2f7",
                background: isSelected
                  ? `${color}10`
                  : isHovered
                  ? `${color}06`
                  : "#ffffff",
                boxShadow: isHovered
                  ? `0 2px 8px ${color}20`
                  : "0 1px 3px rgba(0,0,0,0.04)",
                cursor: "pointer",
                textAlign: "left",
                transition: "all .15s",
                width: "100%",
              }}
            >
              {/* Stage name row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <span
                    style={{
                      width: "9px",
                      height: "9px",
                      borderRadius: "50%",
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#475569",
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {d.stage}
                  </span>
                </div>
                {/* <span
                  style={{
                    fontSize: "11px",
                    color: "#94a3b8",
                    fontWeight: 500,
                    flexShrink: 0,
                    marginLeft: "4px",
                  }}
                >
                  {pct}%
                </span> */}
              </div>

              {/* Big count */}
              <div
                style={{
                  fontSize: "26px",
                  fontWeight: 800,
                  color,
                  lineHeight: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                {d.count.toLocaleString()}
              </div>

              {/* Progress bar */}
              <div
                style={{
                  width: "100%",
                  height: "4px",
                  background: "#f1f5f9",
                  borderRadius: "99px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.max(barPct, 3)}%`,
                    background: color,
                    borderRadius: "99px",
                    transition: "width .4s ease",
                  }}
                />
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
const formatDateTime = (dateValue, timeValue) => {
  const date = toDate(dateValue)
  if (!date) return "-"

  const timeText = timeValue ? ` ${timeValue.toString().slice(0, 5)}` : ""

  return `${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })}${timeText}`
}

const startOfDay = value => {
  const date = toDate(value)
  if (!date) return null
  date.setHours(0, 0, 0, 0)
  return date
}

const endOfDay = value => {
  const date = toDate(value)
  if (!date) return null
  date.setHours(23, 59, 59, 999)
  return date
}

const localDateKey = value => {
  const date = toDate(value)
  if (!date) return null
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

const formatFilterDate = value => {
  const parsed = toDate(value)
  if (!parsed) return ""
  const offset = parsed.getTimezoneOffset() * 60000
  return new Date(parsed.getTime() - offset).toISOString().split("T")[0]
}

const normalizeManagerCandidate = (candidate, stageName) => {
  const stage = normalizeLabel(candidate?.stage || stageName || "Received")
  const name = normalizeLabel(candidate?.candidate_name)
  const role = normalizeLabel(candidate?.job_name)

  return {
    ...candidate,
    name: name === "-" ? "Candidate" : name,
    role: role === "-" ? "Job" : role,
    jobName: role === "-" ? "Job" : role,
    stage,
    addedBy: normalizeLabel(candidate?.added_by),
    dateAdded: candidate?.created_at,
    lastUpdated: candidate?.updated_at || candidate?.created_at,
    status: stage,
    department: "Recruitment",
  }
}

const getCandidateJobs = candidate => {
  const linkedJobs = safeArray(candidate?.linked_jobs)
  if (linkedJobs.length > 0) {
    return linkedJobs
      .map(job => ({
        id: job?.job ?? job?.job_id ?? job?.id ?? "",
        title: job?.job_name || job?.job_title || job?.title || "",
        job_number: job?.job_number || "",
      }))
      .filter(job => job.id || job.title || job.job_number)
  }

  const directTitles = candidate?.job_titles
    ? String(candidate.job_titles)
        .split(",")
        .map(title => title.trim())
        .filter(Boolean)
    : []

  const directIds = candidate?.job_id
    ? String(candidate.job_id)
        .split(",")
        .map(id => id.trim())
        .filter(Boolean)
    : []

  if (directTitles.length > 0 || directIds.length > 0) {
    const length = Math.max(directTitles.length, directIds.length)
    return Array.from({ length }, (_, index) => ({
      id:
        directIds[index] ||
        `${candidate?.candidate_id || "candidate"}-${index}`,
      title: directTitles[index] || candidate?.job_title || "",
      job_number: candidate?.job_number || "",
    })).filter(job => job.id || job.title || job.job_number)
  }

  const singleJobTitle =
    candidate?.job_name ||
    candidate?.jobName ||
    candidate?.job_title ||
    candidate?.role

  if (singleJobTitle) {
    return [
      {
        id: candidate?.job_id || candidate?.candidate_id || "",
        title: singleJobTitle,
        job_number: candidate?.job_number || "",
      },
    ].filter(job => job.id || job.title || job.job_number)
  }

  return []
}

const getOwnerName = candidate =>
  candidate?.addedBy ||
  candidate?.added_by ||
  candidate?.created_by_name ||
  candidate?.owner_name ||
  candidate?.recruiter_name ||
  candidate?.handled_by_name ||
  "Unassigned"

const getJobDisplayName = job =>
  `${job?.job_number ? `${job.job_number} ` : ""}${
    job?.job_title || job?.title || "Job"
  }`
    .trim()
    .replace(/\s+/g, " ")

const getJobSearchText = job =>
  [
    job?.job_number,
    job?.job_title,
    job?.title,
    job?.job_status_name,
    job?.hiring_manager_name,
    safeArray(job?.recruiters_names).join(" "),
  ]
    .join(" ")
    .toLowerCase()

const isInterviewEvent = event => {
  const eventType = normalizeLabel(event?.event_type).toLowerCase()
  const subject = normalizeLabel(event?.subject).toLowerCase()
  return eventType.includes("interview") || subject.includes("interview")
}

const buildParams = params =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
  )

const normalizeDashboardJob = job => ({
  ...job,
  jobTitle:
    job?.job_title ||
    job?.job_name ||
    job?.title ||
    job?.position ||
    "Untitled Job",
  jobNumber: job?.job_order_id || job?.job_number || job?.id || "-",
  openings:
    Number(
      job?.job_openings ??
        job?.no_of_openings ??
        job?.number_of_openings ??
        job?.openings ??
        job?.vacancies ??
        0
    ) || 0,
  status: normalizeLabel(job?.job_status_name || job?.status || "Open"),
  candidates:
    Number(
      job?.candidates_count ??
        job?.candidate_count ??
        job?.total_candidates ??
        job?.candidates ??
        0
    ) || 0,
  recruiter:
    safeArray(job?.recruiters_names).length > 0
      ? safeArray(job.recruiters_names).join(", ")
      : normalizeLabel(
          job?.recruiter_name ||
            job?.hiring_manager_name ||
            job?.created_by_name
        ),
})

const normalizeNameKey = name =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")

const normalizeSortedNameKey = name =>
  normalizeNameKey(name).split(" ").filter(Boolean).sort().join(" ")

const ManagerDashboard = () => {
  document.title = "ATS - Dashboard"

  const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info") || "{}")
  const initialCompanyId =
    userInfos?.user_details?.companies_data?.[0]?.company_id || ""

  const [menuLoaded, setMenuLoaded] = useState(
    localStorage.getItem("menuLoaded") === "true"
  )
  const [loading, setLoading] = useState(true)
  const [companyId, setCompanyId] = useState(initialCompanyId)
  const [showPipelineModal, setShowPipelineModal] = useState(false)
  const [selectedRange, setSelectedRange] = useState("custom")
  const [showCardModal, setShowCardModal] = useState(false)
  const [recentCandidates, setRecentCandidates] = useState([])
  const [loadingRecentCandidates, setLoadingRecentCandidates] = useState(false)
  const [loadingActivity, setLoadingActivity] = useState(false)
  const [activeRecruiterId, setActiveRecruiterId] = useState(null)
  const [recruiterActivityData, setRecruiterActivityData] = useState([])
  const [activityFirst, setActivityFirst] = useState(0)
  const [activityRows, setActivityRows] = useState(10)
  const [activityTotalRecords, setActivityTotalRecords] = useState(0)
  const [applicationsByRole, setApplicationsByRole] = useState([])
  const [loadingApplicationsByRole, setLoadingApplicationsByRole] =
    useState(false)

  const [selectedJobStages, setSelectedJobStages] = useState([])
  const [showJobDialog, setShowJobDialog] = useState(false)
  const [selectedJobTitle, setSelectedJobTitle] = useState("")
  useEffect(() => {
    const fetchApplicationsByRole = async () => {
      try {
        setLoadingApplicationsByRole(true)

        const res = await axios.get("api/v1/dashboard/manager/job-pipeline/", {
          params: {
            company_id: companyId,
          },
        })

        setApplicationsByRole(res?.data?.data || [])
      } catch (err) {
        console.error(err)
        setApplicationsByRole([])
      } finally {
        setLoadingApplicationsByRole(false)
      }
    }

    if (companyId) {
      fetchApplicationsByRole()
    }
  }, [companyId])
  const handleJobClick = job => {
    setSelectedJobTitle(job.job_title)
    setSelectedJobStages(job.stage_breakdown || [])
    setShowJobDialog(true)
  }
  const normalizedApplicationsByRole = useMemo(() => {
    return safeArray(applicationsByRole).map(job => ({
      jobId: job.job_id,
      jobTitle: job.job_title,
      jobNumber: job.job_number,
      status: job.job_status,
      totalCandidates: job.total_candidates,
      stages: safeArray(job.stage_breakdown).map(stage => ({
        stageId: stage.stage_id,
        stageName: stage.stage_name,
        count: stage.count,
        order: stage.order,
      })),
    }))
  }, [applicationsByRole])


  const [selectedCardType, setSelectedCardType] = useState("")
  const handleCardClick = label => {
     if (label === "Shortlisted Candidates" || label === "Rejected Candidates" || label === "Upcoming Interviews") return 
    setSelectedCardType(label)
    setShowCardModal(true)
  }
  const today = new Date()

  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [dateError, setDateError] = useState("")
  const [selectedRecruiter, setSelectedRecruiter] = useState("")
  const [showRecruiterModal, setShowRecruiterModal] = useState(false)
  const [selectedRecruiterName, setSelectedRecruiterName] = useState("")
  const [recruiterJobs, setRecruiterJobs] = useState([])
  const [loadingRecruiterJobs, setLoadingRecruiterJobs] = useState(false)

  const [selectedJob, setSelectedJob] = useState("")

  const [managerDashboardData, setManagerDashboardData] = useState({})
  const [atsUsers, setAtsUsers] = useState([])
  const [jobOrders, setJobOrders] = useState([])
  const [selectedPipelineStage, setSelectedPipelineStage] = useState("")

  useEffect(() => {
    if (menuLoaded) return undefined

    const interval = setInterval(() => {
      const isLoaded = localStorage.getItem("menuLoaded")
      if (isLoaded === "true") {
        setMenuLoaded(true)
        clearInterval(interval)
      }
    }, 300)

    return () => clearInterval(interval)
  }, [menuLoaded])

  // useEffect(() => {
  //   setSelectedRecruiter("")
  //   setSelectedJob("")
  // }, [companyId])
  useEffect(() => {
    const loadRecentCandidates = async () => {
      try {
        setLoadingRecentCandidates(true)

        const response = await axios.get(
          `/api/v1/dashboard/manager/recent-candidates/`,
          {
            params: {
              company_id: companyId,
              recruiter_id: selectedRecruiter || undefined,
              job_id: selectedJob || undefined,
            },
          }
        )

        setRecentCandidates(response?.data?.data || [])
      } catch (error) {
        triggerAlert("error", "Recent candidates failed", error.message)
        setRecentCandidates([])
      } finally {
        setLoadingRecentCandidates(false)
      }
    }

    if (companyId) {
      loadRecentCandidates()
    }
  }, [companyId, selectedRecruiter, selectedJob])

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true)

        const params = {
          company_id: companyId,
          ...(selectedJob ? { job_id: selectedJob } : {}),
          ...(selectedRecruiter ? { recruiter_id: selectedRecruiter } : {}),
        }
        if (startDate && endDate) {
          params.start_date = formatFilterDate(startDate)
          params.end_date = formatFilterDate(endDate)
        }
        if (selectedJob) {
          params.job_id = selectedJob
        }

        const response = await axios.get(`/api/v1/dashboard/manager/stats/`, {
          params,
        })

        setManagerDashboardData(response?.data || {})
      } catch (error) {
        triggerAlert("error", "Dashboard refresh failed", error.message)
      } finally {
        setLoading(false)
      }
    }

    if (companyId) {
      loadDashboard()
    }
  }, [companyId, startDate, endDate, selectedJob, selectedRecruiter])
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const usersResponse = await axios.get("/api/v1/ats-users/", {
          params: { company_id: companyId }, // ← add this
        })

        const users = usersResponse?.data?.users || []

        const formattedUsers = users.map(user => ({
          name: `${user.first_name || ""} ${user.middle_name || ""} ${
            user.last_name || ""
          }`.trim(),
          key: user.id, // ✅ IMPORTANT: this is what you will send to API
        }))

        setAtsUsers(formattedUsers)
      } catch (error) {
        triggerAlert("error", "Dashboard filters failed", error.message)
        setAtsUsers([])
      }
    }

    loadFilterOptions()
  }, [companyId])
  useEffect(() => {
    const loadJobs = async () => {
      try {
        setLoading(true)

        const recruiterId = getRecruiterId(selectedRecruiter)

        const response = await axios.get(`/api/v1/dashboard/manager/jobs/`, {
          params: {
            company_id: companyId, // ✅ ALWAYS send
            ...(recruiterId && { recruiter_id: recruiterId }), // ✅ optional
          },
        })

        const jobsData = response?.data?.data || []

        // ✅ keep raw data (recommended)
        setJobOrders(jobsData)
      } catch (error) {
        triggerAlert("error", "Jobs fetch failed", error.message)
        setJobOrders([])
      } finally {
        setLoading(false)
      }
    }

    // ✅ call only when companyId is available
    if (companyId) {
      loadJobs()
    }
  }, [companyId]) // ✅ IMPORTANT

  const pipelineStages = useMemo(
    () =>
      safeArray(managerDashboardData?.pipeline).sort(
        (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0)
      ),
    [managerDashboardData]
  )

  const candidates = useMemo(
    () =>
      pipelineStages.flatMap(stage =>
        safeArray(stage?.candidates).map(candidate =>
          normalizeManagerCandidate(candidate, stage?.stage_name)
        )
      ),
    [pipelineStages]
  )

  const recentJobs = useMemo(() => jobOrders, [jobOrders])
  const [interviews, setInterviews] = useState([])
  const [loadingInterviews, setLoadingInterviews] = useState(false)
  useEffect(() => {
    const loadInterviews = async () => {
      try {
        setLoadingInterviews(true)

        const response = await axios.get(
          `/api/v1/dashboard/manager/interviews/`,
          {
            params: {
              company_id: companyId,
            },
          }
        )

        setInterviews(response?.data?.interviews || [])
      } catch (error) {
        triggerAlert("error", "Interviews fetch failed", error.message)
        setInterviews([])
      } finally {
        setLoadingInterviews(false)
      }
    }

    if (companyId) {
      loadInterviews()
    }
  }, [companyId])
  const events = useMemo(() => interviews, [interviews])

  const recruiterIdByName = useMemo(() => {
    const map = new Map()
    atsUsers.forEach(user => {
      const names = [
        user?.name,
        user?.label,
        user?.full_name,
        [user?.first_name, user?.middle_name, user?.last_name]
          .map(part => String(part || "").trim())
          .filter(Boolean)
          .join(" "),
      ].filter(Boolean)

      names.forEach(name => {
        map.set(normalizeNameKey(name), user.id)
        map.set(normalizeSortedNameKey(name), user.id)
      })
    })
    return map
  }, [atsUsers])

  const getRecruiterId = useCallback(
    recruiterName =>
      recruiterIdByName.get(normalizeNameKey(recruiterName)) ||
      recruiterIdByName.get(normalizeSortedNameKey(recruiterName)) ||
      "",
    [recruiterIdByName]
  )

  const recruiterOptions = useMemo(() => {
    const names = Array.from(
      new Set(atsUsers.map(user => user.name).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b))

    return [
      { name: "All Recruiters", key: "" },
      ...atsUsers.map(user => ({ name: user.name, key: user.key })),
    ]
  }, [atsUsers])
  const jobOptions = useMemo(() => {
    return [
      { name: "All Jobs", key: "" },
      ...safeArray(jobOrders).map(job => ({
        name: job.job_title || "Untitled Job",
        key: job.job_id,
      })),
    ]
  }, [jobOrders])

  const filteredCandidates = useMemo(() => {
    return safeArray(candidates).filter(candidate => {
      const recruiterName = getOwnerName(candidate)
      const recruiterMatches =
        !selectedRecruiter || recruiterName === selectedRecruiter

      const jobMatches = !selectedJob
        ? true
        : getCandidateJobs(candidate).some(
            job => String(job?.id) === String(selectedJob)
          )

      return recruiterMatches && jobMatches
    })
  }, [candidates, selectedRecruiter, selectedJob])

  const filteredJobs = useMemo(() => {
    return safeArray(recentJobs).filter(job => {
      const recruiterText = safeArray(job?.recruiters_names)
        .join(" ")
        .toLowerCase()

      const managerText = normalizeLabel(job?.hiring_manager_name).toLowerCase()

      const jobMatches = !selectedJob
        ? true
        : String(job?.job_id) === String(selectedJob)

      const recruiterMatches = !selectedRecruiter
        ? true
        : String(job?.recruiter_id || job?.created_by || "") ===
          String(selectedRecruiter)

      return recruiterMatches && jobMatches
    })
  }, [recentJobs, selectedRecruiter, selectedJob])

  const candidateMap = useMemo(() => {
    return new Map(
      safeArray(candidates).map(candidate => [
        String(candidate?.candidate_id),
        candidate,
      ])
    )
  }, [candidates])

  const filteredEvents = useMemo(() => {
    const today = startOfDay(new Date())
    const customStart = startDate ? startOfDay(startDate) : null
    const customEnd = endDate ? endOfDay(endDate) : null

    return safeArray(events)
      .filter(event => isInterviewEvent(event))
      .filter(event => {
        const eventDate = startOfDay(event?.interview_date)
        if (!eventDate) return false
        if (eventDate < today) return false
        if (customStart && eventDate < customStart) return false
        if (customEnd && eventDate > customEnd) return false

        const candidateId =
          event?.candidate_ids?.[0]?.id || event?.candidate_id || ""
        const candidate = candidateMap.get(String(candidateId))
        const recruiterName = candidate ? getOwnerName(candidate) : ""

        const recruiterMatches = !selectedRecruiter
          ? true
          : recruiterName === selectedRecruiter

        const jobLabel =
          event?.job_ids?.[0]?.name ||
          event?.job_ids?.[0]?.job_title ||
          event?.job_ids?.[0]?.title ||
          ""

        const jobMatches = !selectedJob
          ? true
          : String(jobLabel).toLowerCase().includes(selectedJob.toLowerCase())

        return recruiterMatches && jobMatches
      })
      .sort((a, b) => {
        const dateA = new Date(a?.interview_time)
        const dateB = new Date(b?.interview_time)
        return dateA - dateB
      })
  }, [events, candidateMap, selectedRecruiter, selectedJob, startDate, endDate])

  const stageNames = useMemo(() => {
    const names = pipelineStages
      .map(stage => normalizeLabel(stage?.stage_name))
      .filter(stage => stage !== "-")

    return names.length > 0
      ? names
      : [
          "Received",
          "Interview",
          "Offered",
          "Rejected",
          "Placed",
          "Panel Round",
          "Shortlisted",
          "Documentation",
          "Task Assessment",
          "Hold",
          "Responded",
          "Not Responded",
          "Screening Round",
          "L1 Round",
          "L2 Round",
        ]
  }, [pipelineStages])

  const getStageName = candidate => {
    const stageText = normalizeLabel(
      candidate?.stage ||
        candidate?.candidate_pipeline_status ||
        candidate?.pipeline_status ||
        candidate?.status ||
        candidate?.candidate_hrms_status ||
        ""
    ).toLowerCase()

    if (
      stageText.includes("received") ||
      stageText.includes("applied") ||
      stageText.includes("new")
    )
      return "Received"
    if (stageText.includes("panel")) return "Panel Round"
    if (stageText.includes("not responded")) return "Not Responded"
    if (stageText.includes("responded")) return "Responded"
    if (stageText.includes("screen")) return "Screening Round"
    if (stageText.includes("l1")) return "L1 Round"
    if (stageText.includes("l2")) return "L2 Round"
    if (stageText.includes("shortlist")) return "Shortlisted"
    if (stageText.includes("documentation") || stageText.includes("document"))
      return "Documentation"
    if (stageText.includes("task") || stageText.includes("assessment"))
      return "Task Assessment"
    if (stageText.includes("hold")) return "Hold"
    if (stageText.includes("reject") || stageText.includes("closed"))
      return "Rejected"
    if (
      stageText.includes("place") ||
      stageText.includes("hire") ||
      stageText.includes("select")
    )
      return "Placed"
    if (stageText.includes("offer")) return "Offered"
    if (stageText.includes("interview") || stageText.includes("final"))
      return "Interview"
    return "Received"
  }

  const getCandidateName = candidate => {
    if (candidate?.name) return candidate.name
    const firstName = candidate?.first_name || ""
    const lastName = candidate?.last_name || ""
    const fullName = `${firstName} ${lastName}`.trim()
    return fullName || candidate?.email || "Candidate"
  }

  const getCandidateRole = candidate => {
    const jobs = getCandidateJobs(candidate)
    return (
      candidate?.role ||
      jobs?.[0]?.title ||
      candidate?.job_title ||
      "Senior Software Engineer"
    )
  }
const fetchRecruiterActivity = useCallback(async (
  recruiterId = "",
  jobId = "",
  first = activityFirst,
  rows = activityRows
) => {
  try {
    setLoadingActivity(true)
    const pageNumber = Math.floor(first / rows) + 1
    const res = await axios.get("api/v1/dashboard/manager/job-handlers/", {
      params: {
        company_id: companyId,
        recruiter_id: recruiterId || undefined,
        job_id: jobId || undefined,          // ← add this
        startDate: selectedRange === "custom" && startDate ? formatFilterDate(startDate) : undefined,
        endDate: selectedRange === "custom" && endDate ? formatFilterDate(endDate) : undefined,
        page: pageNumber,
        size: rows,
      },
    })
    const responseData = res?.data || {}
    const rawRows = Array.isArray(responseData?.data)
      ? responseData.data
      : Array.isArray(responseData?.data?.results)
        ? responseData.data.results
        : Array.isArray(responseData?.results)
          ? responseData.results
          : []
    const totalRecords =
      responseData?.count ??
      responseData?.total_records ??
      responseData?.totalRecords ??
      responseData?.total ??
      responseData?.data?.count ??
      rawRows.length

    const formatted = rawRows.map(item => ({
      recruiter: item.recruiter || item.recruiter_name || "-",
      role: item.role || item.job_title || "-",
      candidatesAdded: item.candidatesAdded || item.candidates_added || 0,
      testsSent: item.tests_sent || item.testsSent || 0,
      clickToCallCount: item.click_to_calls || item.clickToCallCount || 0,
      screeningCallCount: item.screening_call_count || item.screeningCallCount || 0,
      shortlistedCount: item.shortlisted_count || item.shortlistedCount || 0,
      rejectedCount: item.rejected_count || item.rejectedCount || 0,
    }))
    setRecruiterActivityData(formatted)
    setActivityTotalRecords(Number(totalRecords) || formatted.length)
  } catch (err) {
    console.error(err)
    setRecruiterActivityData([])
    setActivityTotalRecords(0)
  } finally {
    setLoadingActivity(false)
  }
}, [activityFirst, activityRows, companyId, startDate, endDate, selectedRange])
useEffect(() => {
  if (!companyId) return
  fetchRecruiterActivity(
    selectedRecruiter,
    selectedJob,
    activityFirst,
    activityRows
  )
}, [
  activityFirst,
  activityRows,
  companyId,
  fetchRecruiterActivity,
  selectedJob,
  selectedRecruiter,
  startDate,
  endDate,
])

const handleJobChange = (jobId) => {
  const normalizedId = jobId ?? ""
  setSelectedJob(normalizedId)
  setActivityFirst(0)
}

const handleActivityPageChange = event => {
  setActivityFirst(event.first)
  setActivityRows(event.rows)
}

const displayCandidates = useMemo(() => {
    return filteredCandidates.map(candidate => ({
      ...candidate,
      name: getCandidateName(candidate),
      role: getCandidateRole(candidate),
      stage: getStageName(candidate),
      addedBy: candidate?.addedBy || getOwnerName(candidate),
      dateAdded: candidate?.dateAdded || candidate?.created_at,
      lastUpdated:
        candidate?.lastUpdated ||
        candidate?.updated_at ||
        candidate?.modified_at ||
        candidate?.created_at,
      status:
        candidate?.status ||
        candidate?.candidate_hrms_status ||
        getStageName(candidate),
      department:
        candidate?.department || candidate?.department_name || "Recruitment",
    }))
  }, [filteredCandidates])
  const stageSummary = useMemo(() => {
    const counts = stageNames.reduce(
      (acc, stage) => ({ ...acc, [stage]: 0 }),
      {}
    )
    if (pipelineStages.length > 0 && !selectedRecruiter && !selectedJob) {
      pipelineStages.forEach(item => {
        const stage = normalizeLabel(item?.stage_name)
        counts[stage] = Number(item?.count) || 0
      })
    } else {
      displayCandidates.forEach(candidate => {
        counts[candidate.stage] = (counts[candidate.stage] || 0) + 1
      })
    }

    const total =
      Object.values(counts).reduce((sum, count) => sum + count, 0) || 1
    return stageNames.map(stage => ({
      stage,
      count: counts[stage] || 0,
      percent: Math.round(((counts[stage] || 0) / total) * 100),
    }))
  }, [
    displayCandidates,
    pipelineStages,
    selectedRecruiter,
    selectedJob,
    stageNames,
  ])

  const upcomingInterviews = useMemo(() => {
    return safeArray(interviews)
      .filter(i => i.status === "Scheduled")
      .sort((a, b) => new Date(a.interview_date) - new Date(b.interview_date))
      .slice(0, 8)
      .map(i => ({
        candidateName: i?.candidate_name || "Candidate",
        position: i?.job_title || "Job",
        interviewTime: i?.interview_time || "-",
        interviewer:
          safeArray(i?.interviewers)
            .map(x => x.name)
            .join(", ") || "Hiring team",
        meetingType: i?.interview_type || "Interview",
      }))
  }, [interviews])

  const recruiterActivity = useMemo(() => {
    const byRecruiter = new Map()
    displayCandidates.forEach(candidate => {
      const recruiter = candidate.addedBy || "Unassigned"
      if (!byRecruiter.has(recruiter)) {
        byRecruiter.set(recruiter, {
          recruiter,
          handled: 0,
          interviewsScheduled: 0,
          pendingFeedback: 0,
          latestCandidate: "",
        })
      }

      const bucket = byRecruiter.get(recruiter)
      bucket.handled += 1
      if (["Interview", "Panel Round"].includes(candidate.stage))
        bucket.interviewsScheduled += 1
      if (
        [
          "Shortlisted",
          "Panel Round",
          "Documentation",
          "Task Assessment",
          "Hold",
        ].includes(candidate.stage)
      ) {
        bucket.pendingFeedback += 1
      }
      if (
        !bucket.latestCandidate ||
        (toDate(candidate.lastUpdated)?.getTime() || 0) >
          (toDate(bucket.latestDate)?.getTime() || 0)
      ) {
        bucket.latestCandidate = candidate.name
        bucket.latestDate = candidate.lastUpdated
      }
    })

    return Array.from(byRecruiter.values()).sort(
      (a, b) => b.handled - a.handled
    )
  }, [displayCandidates])

  const dashboardStats = managerDashboardData?.stats || {}
  const getStatCount = key => {
    const rawValue = dashboardStats?.[key]?.count
    if (rawValue === undefined || rawValue === null) return null
    const numericValue = Number(rawValue)
    return Number.isFinite(numericValue) ? numericValue : null
  }
  const totalCandidates =
    getStatCount("total_candidates") ?? displayCandidates.length
  const derivedNewToday = displayCandidates.filter(
    candidate => localDateKey(candidate.dateAdded) === localDateKey(new Date())
  ).length
  const newToday = getStatCount("new_applications_today") ?? derivedNewToday
  const upcomingInterviewCount = getStatCount("upcoming_interviews") ?? 0
  const openingsByJob = useMemo(
    () =>
      safeArray(jobOrders)
        .map(job => {
          const openings =
            Number(
              job?.no_of_openings ??
                job?.number_of_openings ??
                job?.openings ??
                job?.vacancies ??
                0
            ) || 0

          const jobTitle =
            job?.job_title || job?.title || job?.position || "Untitled Job"
          const jobNumber =
            job?.job_order_id || job?.job_number || job?.id || "-"

          return {
            jobTitle,
            jobNumber,
            openings,
            status: normalizeLabel(
              job?.job_status_name || job?.status || "Open"
            ),
            recruiter:
              safeArray(job?.recruiters_names).length > 0
                ? safeArray(job.recruiters_names).join(", ")
                : normalizeLabel(
                    job?.hiring_manager_name || job?.created_by_name
                  ),
          }
        })
        .filter(job => job.openings > 0)
        .sort((a, b) => b.openings - a.openings),
    [jobOrders]
  )
  const derivedTotalOpenings = openingsByJob.reduce(
    (sum, job) => sum + (Number(job?.openings) || 0),
    0
  )
  const totalOpenings = getStatCount("job_openings") ?? derivedTotalOpenings
  const interviewCount =
    (stageSummary.find(item => item.stage === "Interview")?.count || 0) +
      (stageSummary.find(item => item.stage === "Panel Round")?.count || 0) ||
    upcomingInterviews.length
  const hiredCount =
    stageSummary.find(item => item.stage === "Placed")?.count || 0
  const rejectedCount =
    stageSummary.find(item => item.stage === "Rejected")?.count || 0
    

  const summaryCards = [
    {
      label: "Total Candidates",
      value: totalCandidates,
      note: "Total Candidates",
      icon: UsersRound,
    },
    {
      label: "New Applications Today",
      value: newToday,
      note: dashboardStats?.new_applications_today?.label || "Added today",
      icon: FileText,
    },
    // {
    //   label: "Upcoming Interviews",
    //   value: upcomingInterviewCount,
    //   note: dashboardStats?.upcoming_interviews?.label || "Upcoming and active",
    //   icon: CalendarCheck,
    // },
    {
      label: "No. of Openings",
      value: totalOpenings,
      note: dashboardStats?.job_openings?.label || "Active Job Openings",
      icon: FileText,
    },
    {
      label: "Shortlisted Candidates",
      value: interviewCount,
      note: "Interview and panel rounds",
      icon: Clock3,
    },
    {
      label: "Rejected Candidates",
      value: rejectedCount,
      note: "Rejected Candidates",
      icon: XCircle,
    },
    {
      label: "Placed Candidates",
      value: hiredCount,
      note: "Placed Candidates",
      icon: CheckCircle2,
    },
  ]

  const recentCandidateActivity = useMemo(() => {
    return safeArray(recentCandidates).map(item => ({
      name: item?.candidate_name || "Candidate",
      role: item?.job_title || "Job",
      stage: item?.stage || "Received",
      addedBy: item?.created_by || "Unassigned",
      dateAdded: item?.created_at || "",
    }))
  }, [recentCandidates])

  const selectedPipelineCandidates = useMemo(() => {
    if (!selectedPipelineStage) return []
    return displayCandidates
      .filter(candidate => candidate.stage === selectedPipelineStage)
      .sort(
        (a, b) =>
          (toDate(b.lastUpdated)?.getTime() || 0) -
          (toDate(a.lastUpdated)?.getTime() || 0)
      )
      .slice(0, 25)
  }, [displayCandidates, selectedPipelineStage])

  const recruiterJobCoverage = useMemo(() => {
    const byRecruiterRole = new Map()

    displayCandidates.forEach(candidate => {
      const recruiter = candidate.addedBy || "Unassigned"
      const role = candidate.role || "Senior Software Engineer"
      const key = `${recruiter}__${role}`

      if (!byRecruiterRole.has(key)) {
        byRecruiterRole.set(key, {
          recruiter,
          recruiterId: getRecruiterId(recruiter),
          role,
          candidatesAdded: 0,
          interviews: 0,
          pendingReview: 0,
          latestAdded: null,
        })
      }

      const bucket = byRecruiterRole.get(key)
      bucket.candidatesAdded += 1
      if (["Interview", "Panel Round"].includes(candidate.stage))
        bucket.interviews += 1
      if (
        [
          "Shortlisted",
          "Panel Round",
          "Documentation",
          "Task Assessment",
          "Hold",
        ].includes(candidate.stage)
      ) {
        bucket.pendingReview += 1
      }

      const addedDate = toDate(candidate.dateAdded)
      if (
        addedDate &&
        (!bucket.latestAdded || addedDate > bucket.latestAdded)
      ) {
        bucket.latestAdded = addedDate
      }
    })

    return Array.from(byRecruiterRole.values())
      .sort((a, b) => b.candidatesAdded - a.candidatesAdded)
      .slice(0, 8)
  }, [displayCandidates, getRecruiterId])

  const handleRecruiterChange = async recruiterId => {
    console.info("Selected recruiter ID:", recruiterId) // Debug log
  const normalizedId = recruiterId ?? ""
  setActivityFirst(0)
  setSelectedJob("")
  setSelectedRecruiter(normalizedId)

  if (!normalizedId) {
    // reset to all jobs when recruiter is cleared
    setJobOrders([])
    // re-fetch all jobs
    try {
      const response = await axios.get(`/api/v1/dashboard/manager/jobs/`, {
        params: { company_id: companyId },
      })
      setJobOrders(response?.data?.data || [])
    } catch (e) {
      setJobOrders([])
    }
    return
  }

  // fetch jobs for selected recruiter
  try {
    const response = await axios.get("/api/v1/dashboard/manager/jobs/", {
      params: {
        recruiter_id: normalizedId,
        company_id: companyId || initialCompanyId,
      },
    })

    const jobs = safeArray(response?.data?.data || [])
    setJobOrders(jobs)      // ← this updates jobOptions dropdown automatically
    setSelectedJob("")      // ← reset selected job when recruiter changes
  } catch (error) {
    triggerAlert("error", "Recruiter jobs failed", error.message)
    setJobOrders([])
  }
}

  const roleSummary = useMemo(() => {
    const byRole = new Map()
    displayCandidates.forEach(candidate => {
      const role = candidate.role || "Senior Software Engineer"
      if (!byRole.has(role)) {
        byRole.set(role, {
          role,
          total: 0,
          interviews: 0,
          selected: 0,
          rejected: 0,
          pending: 0,
        })
      }

      const bucket = byRole.get(role)
      bucket.total += 1
      if (candidate.stage === "Interview" || candidate.stage === "Panel Round")
        bucket.interviews += 1
      if (candidate.stage === "Placed") bucket.selected += 1
      if (candidate.stage === "Rejected") bucket.rejected += 1
      if (
        [
          "Shortlisted",
          "Panel Round",
          "Documentation",
          "Task Assessment",
          "Hold",
        ].includes(candidate.stage)
      ) {
        bucket.pending += 1
      }
    })

    return Array.from(byRole.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)
  }, [displayCandidates])

  const jobActivity = useMemo(() => {
    const jobs = filteredJobs.length > 0 ? filteredJobs : recentJobs
    return safeArray(jobs)
      .slice(0, 8)
      .map(job => ({
        role: getJobDisplayName(job),
        status: normalizeLabel(job?.job_status_name || "Open"),
        openings: job?.openings || "-",
        hiringManager:
          safeArray(job?.recruiters_names).length > 0
            ? safeArray(job.recruiters_names).join(", ")
            : normalizeLabel(job?.hiring_manager_name),
        deadline:
          job?.close_date || job?.application_deadline || job?.job_closing_date,
      }))
  }, [filteredJobs, recentJobs])

  const resetFilters = () => {
    setSelectedRange("custom")
    setStartDate(null)
    setEndDate(null)
    setDateError("")
    setSelectedRecruiter("")
    setSelectedJob("")
  }
 const cardData = useMemo(() => {
  switch (selectedCardType) {
   case "Total Candidates":
  return safeArray(dashboardStats?.total_candidates?.candidate_detail).map(c => ({
    name: `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Candidate",
    email: c.email || "-",
    candidateId: c.candidate_id || "-",
    companyId: c.company_id || "-",
    createdBy: c.created_by_id || "-",
    status: c.status || "-",
  }))

    case "New Applications Today":
      return safeArray(dashboardStats?.new_applications_today?.applications_detail).map(c => ({
        name: `${c.candidate?.first_name || ""} ${c.candidate?.last_name || ""}`.trim() || "Candidate",
        email: c.candidate?.email || "-",
        candidateId: c.candidate?.candidate_id || "-",
        role: `Job #${c.job_id}`,
        stage: c.stage || "Received",
        dateAdded: c.created_at || "",
        addedBy: String(c.created_by_id || "-"),
      }))

    case "Upcoming Interviews": {
      const apiInterviews = safeArray(
        dashboardStats?.upcoming_interviews?.interviews_detail
      )
      const source = apiInterviews.length > 0 ? apiInterviews : upcomingInterviews

      return source.map(interview => ({
        candidateName:
          interview?.candidate_name ||
          interview?.candidateName ||
          `${interview?.candidate?.first_name || ""} ${
            interview?.candidate?.last_name || ""
          }`.trim() ||
          "Candidate",
        position:
          interview?.job_title || interview?.position || interview?.role || "-",
        interviewTime:
          interview?.interview_time ||
          interview?.interviewTime ||
          interview?.scheduled_at ||
          interview?.start_time ||
          "",
        interviewer:
          interview?.interviewer ||
          interview?.interviewer_name ||
          interview?.created_by ||
          "-",
        meetingType:
          interview?.meeting_type || interview?.meetingType || interview?.type || "-",
        status: interview?.status || "-",
      }))
    }

    case "No. of Openings":
      return safeArray(dashboardStats?.job_openings?.job_openings_detail).map(j => ({
        jobTitle: j.job_title || "Untitled",
        jobNumber: String(j.job_id || "-"),
        openings: j.openings ?? "-",
        status: j.status === 3 ? "Active" : String(j.status),
        recruiter: String(j.created_by || "-"),
      }))

    case "Shortlisted Candidates":
      return displayCandidates.filter(c => c.stage === "Shortlisted")

    case "Rejected Candidates":
      return displayCandidates.filter(c => c.stage === "Rejected")

    case "Placed Candidates":
      return displayCandidates.filter(c => c.stage === "Placed")

    default:
      return []
  }
}, [selectedCardType, displayCandidates, dashboardStats, openingsByJob, upcomingInterviews])

 const cardDialogHeader = useMemo(() => {
  if (selectedCardType === "Total Candidates") {
    return `Total Candidates (${dashboardStats?.total_candidates?.count ?? cardData.length})`
  }
  if (selectedCardType === "New Applications Today") {
    return `New Applications Today (${dashboardStats?.new_applications_today?.count ?? cardData.length})`
  }
  if (selectedCardType === "Upcoming Interviews") {
    return `Upcoming Interviews (${dashboardStats?.upcoming_interviews?.count ?? cardData.length})`
  }
  if (selectedCardType === "No. of Openings") {
    return `No. of Openings (${dashboardStats?.job_openings?.count ?? totalOpenings})`
  }
  return `${selectedCardType} (${cardData.length})`
}, [selectedCardType, cardData, dashboardStats, totalOpenings])

  const cardDialogColumns = useMemo(() => {
    if (selectedCardType === "No. of Openings") {
      return [
        { field: "jobTitle", header: "Job" },
        { field: "jobNumber", header: "Job ID" },
        { field: "openings", header: "Openings" },
        { field: "status", header: "Status" },
        { field: "recruiter", header: "Created By" },
      ]
    }

    if (selectedCardType === "Total Candidates") {
      return [
        { field: "name", header: "Candidate" },
        { field: "email", header: "Email" },
        { field: "candidateId", header: "Candidate ID" },
        { field: "companyId", header: "Company ID" },
        { field: "createdBy", header: "Created By" },
        { field: "status", header: "Status" },
      ]
    }

    if (selectedCardType === "Upcoming Interviews") {
      return [
        { field: "candidateName", header: "Candidate Name" },
        { field: "position", header: "Position" },
        { field: "interviewTime", header: "Interview Time", isDate: true },
        { field: "interviewer", header: "Interviewer" },
        { field: "meetingType", header: "Meeting Type" },
        { field: "status", header: "Status" },
      ]
    }

    return [
      { field: "name", header: "Candidate" },
      { field: "email", header: "Email" },
      { field: "candidateId", header: "Candidate ID" },
      { field: "role", header: "Job Applied For" },
      { field: "stage", header: "Stage", isStage: true },
      { field: "dateAdded", header: "Date Added", isDate: true },
      { field: "addedBy", header: "Added By" },
    ]
  }, [selectedCardType])

  const renderCardCell = (row, column) => {
    const value = column.isDate
      ? formatDate(row?.[column.field])
      : row?.[column.field] ?? "-"

    if (column.isStage) {
      const stage = value || "-"
      return (
        <span
          className={`manager-badge manager-badge-${String(stage)
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
        >
          {stage}
        </span>
      )
    }

    if (column.field === "name" || column.field === "candidateName" || column.field === "jobTitle") {
      return <span className="manager-table-strong">{String(value || "-")}</span>
    }

    return String(value || "-")
  }
  const handleEndDateChange = event => {
    const newEndDate = event.value
    if (startDate && newEndDate && newEndDate < startDate) {
      setDateError("End date cannot be earlier than start date")
      setEndDate(null)
      return
    }

    setDateError("")
    setEndDate(newEndDate)
  }

  if (!menuLoaded) {
    return (
      <div className="page-content manager-dashboard-page">
        <Container fluid>
          <OrgTableShimmer columns={6} rows={8} titleWidth="260px" />
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content manager-dashboard-page">
      <Container fluid>
        <div className="manager-dashboard-shell">
          <div className="manager-dashboard-hero">
            <div className="manager-dashboard-hero-copy">
              <h1 className="manager-dashboard-title">Manager Dashboard</h1>
              <p className="manager-dashboard-subtitle">
                A clear view of candidates, interview plans, recruiter activity,
                and the next actions your hiring team should handle today.
              </p>
              <div className="manager-dashboard-meta">
                <span> Total Candidates : {totalCandidates}</span>
                {/* <span>{upcomingInterviews.length} upcoming interviews</span> */}
              </div>
            </div>

            <div className="manager-dashboard-company-card">
              <label className="manager-dashboard-label">Select Company</label>

              <div style={{ width: "220px" }}>
                <CompanySelector
                  value={companyId}
                  onCompanyChange={c => {
                    const selectedId =
                      c?.company_id === "All" ? "" : c?.company_id ?? ""
                    // Reset all filters synchronously — React batches these into one render
                    setCompanyId(selectedId)
                    setSelectedRecruiter("")
                    setSelectedJob("")
                    setRecruiterJobs([])
                    setRecruiterActivityData([])
                  }}
                  showAllOption
                />
              </div>

              <p className="manager-dashboard-help">
                Filter the dashboard by company, team, recruiter, role, or date
                range.
              </p>
            </div>
          </div>

          <Card className="manager-dashboard-filter-card">
            <div className="manager-filter-grid">
              {/* <div className="manager-filter-field">
                <label className="manager-dashboard-label">Department</label>
                <Dropdown
                  value={selectedDepartment}
                  onChange={e => setSelectedDepartment(e.value)}
                  options={departmentOptions}
                  optionLabel="name"
                  optionValue="key"
                  placeholder="All Departments"
                  filter
                  className="manager-dashboard-dropdown"
                  panelClassName="manager-dashboard-dropdown-panel"
                />
              </div> */}
              <div className="manager-filter-field">
                <label className="manager-dashboard-label">
                  Select Recruiter
                </label>
                <Dropdown
                  value={selectedRecruiter || ""}
                  onChange={e => handleRecruiterChange(e.value ?? "")}
                  options={recruiterOptions}
                  optionLabel="name"
                  optionValue="key"
                  placeholder="All Recruiters"
                  filter
                  showClear
                  className="manager-dashboard-dropdown"
                  panelClassName="manager-dashboard-dropdown-panel"
                />
              </div>
              <div className="manager-filter-field">
                <label className="manager-dashboard-label">Job</label>
                <Dropdown
                  value={selectedJob}
                  onChange={e => handleJobChange(e.value)} 
                  options={jobOptions}
                  optionLabel="name"
                  optionValue="key"
                  placeholder="All Jobs"
                  filter
                  showClear
                  className="manager-dashboard-dropdown"
                  panelClassName="manager-dashboard-dropdown-panel"
                />
              </div>
              {/* <div className="manager-filter-field">
                <label className="manager-dashboard-label">Range</label>
                <Dropdown
                  value={selectedRange}
                  onChange={e => setSelectedRange(e.value)}
                  options={rangeOptions}
                  optionLabel="name"
                  optionValue="keyID"
                  placeholder="Custom Range"
                  className="manager-dashboard-dropdown"
                  panelClassName="manager-dashboard-dropdown-panel"
                />
              </div> */}
              {selectedRange === "custom" && (
                <>
                  <div className="manager-filter-field manager-filter-date">
                    <label className="manager-dashboard-label">From</label>
                    <Calendar
                      value={startDate}
                      onChange={e => setStartDate(e.value)}
                      placeholder="Start Date"
                      showIcon
                      dateFormat="dd-mm-yy"
                      className="manager-dashboard-calendar"
                    />
                  </div>
                  <div className="manager-filter-field manager-filter-date">
                    <label className="manager-dashboard-label">To</label>
                    <Calendar
                      value={endDate}
                      onChange={handleEndDateChange}
                      placeholder="End Date"
                      showIcon
                      dateFormat="dd-mm-yy"
                      className={`manager-dashboard-calendar ${
                        dateError ? "p-invalid" : ""
                      }`}
                    />
                  </div>
                </>
              )}
              <div className="manager-filter-field manager-filter-action">
                <button
                  type="button"
                  className="btn  btn-primary btn-md blueBtn"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>
            </div>
            {dateError && (
              <div className="manager-dashboard-error">{dateError}</div>
            )}
          </Card>

          <div className="manager-summary-grid">
            {summaryCards.map(item => (
              <Card
                className="manager-stat-card manager-stat-card-clickable"
                key={item.label}
                role="button"
                tabIndex={loading ? -1 : 0}
                // onClick={() => !loading && handleCardClick(item.label)}
                onKeyDown={event => {
                  if (loading) return
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    handleCardClick(item.label)
                  }
                }}
              >
                <div className="manager-stat-topline">
                  <span className="manager-stat-icon">
                    <item.icon size={18} strokeWidth={2} />
                  </span>
                  <span className="manager-stat-label">{item.label}</span>
                </div>
                <div className="manager-stat-value">
                  {loading ? "..." : item.value}{" "}
                  {/* ← show dots while loading */}
                </div>
                <span className="manager-stat-note">{item.note}</span>
              </Card>
            ))}
          </div>

          <Row className="g-2 mt-1 manager-dashboard-row">
            <Col xl={6}>
              <Card
                className="manager-panel manager-panel-compact manager-pipeline-panel"
                style={{
                  height: "100%",
                  overflow: "hidden",
                  boxSizing: "border-box",
                }}
              >
                <div className="manager-panel-header" style={{ flexShrink: 0 }}>
                  <div>
                    <div className="manager-panel-kicker">
                      Candidate Pipeline
                    </div>
                    <h2 className="manager-panel-title">
                      Where candidates are right now
                    </h2>
                  </div>
                </div>
                <div style={{ overflow: "hidden", minHeight: 0 }}>
                  <PipelineChart
                    stageSummary={stageSummary}
                    onStageClick={stage => {
                      setSelectedPipelineStage(stage)
                      setShowPipelineModal(true)
                    }}
                    selectedPipelineStage={selectedPipelineStage}
                  />
                </div>
              </Card>
            </Col>

            <Col xl={6}>
              <Card className="manager-panel manager-panel-compact manager-panel-table manager-coverage-panel">
                <div className="manager-panel-header">
                  <div>
                    <div className="manager-panel-kicker">
                      Recruiter Job Activity
                    </div>
                  </div>
                </div>
                <DataTable
                  value={recruiterActivityData}
                  loading={loadingActivity}
                  lazy
                  paginator
                  first={activityFirst}
                  rows={activityRows}
                  totalRecords={activityTotalRecords}
                  onPage={handleActivityPageChange}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  responsiveLayout="scroll"
                  scrollable
                  scrollHeight="385px"
                  emptyMessage="No recruiter job activity found."
                  className="manager-table"
                >
                  <Column
                    field="recruiter"
                    header="Recruiter"
                    frozen
                    style={{
                      minWidth: "140px",
                      maxWidth: "140px",
                      width: "140px",
                    }}
                    body={row => (
                      <span
                        className="manager-table-strong"
                        style={{
                          cursor: "pointer",
                          color: "#0d6efd",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {row.recruiter}
                      </span>
                    )}
                  />
                  <Column
                    field="role"
                    header="Job Title"
                    style={{
                      minWidth: "160px",
                      maxWidth: "160px",
                      width: "160px",
                    }}
                    body={row => (
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {row.role}
                      </span>
                    )}
                  />
                  <Column field="candidatesAdded" header="Candidates Added" />
                  <Column field="testsSent" header="Tests Sent" />
                  <Column field="clickToCallCount" header="Click to Call" />
                  <Column field="screeningCallCount" header="Screening Calls" />
                  <Column field="shortlistedCount" header="Shortlisted" />
                  <Column field="rejectedCount" header="Rejected" />
                </DataTable>
              </Card>
            </Col>
          </Row>

          {/* {selectedPipelineStage && (
            <Row className="g-2 mt-1 manager-dashboard-row">
              <Col lg={12}>
                <Card className="manager-panel manager-panel-table manager-pipeline-candidates-panel">
                  <div className="manager-panel-header">
                    <div>
                      <div className="manager-panel-kicker">Pipeline Candidates</div>
                      <h2 className="manager-panel-title">
                        {selectedPipelineStage} candidates ({selectedPipelineCandidates.length})
                      </h2>
                    </div>
                    <button
                      type="button"
                      className="manager-panel-clear"
                      onClick={() => setSelectedPipelineStage("")}
                    >
                      Clear
                    </button>
                  </div>

                  <DataTable
                    value={selectedPipelineCandidates}
                    responsiveLayout="scroll"
                    scrollable
                    scrollHeight="260px"
                    emptyMessage={`No candidates found in ${selectedPipelineStage}.`}
                    className="manager-table"
                  >
                    <Column field="name" header="Candidate" body={row => <span className="manager-table-strong">{row.name}</span>} />
                    <Column field="role" header="Role Applied For" />
                    <Column field="addedBy" header="Added By" />
                    <Column header="Date Added" body={row => formatDate(row.dateAdded)} />
                    <Column header="Last Updated" body={row => formatDate(row.lastUpdated)} />
                    <Column field="status" header="Status" />
                  </DataTable>
                </Card>
              </Col>
            </Row>
          )} */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              padding: "8px 12px",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#1e40af",
              lineHeight: "1.6",
            }}
          >
            <Info
              size={14}
              strokeWidth={2}
              style={{ marginTop: "2px", flexShrink: 0, color: "#3b82f6" }}
            />
            <span>
              The Below tables reflects candidates added within the selected{" "}
              <strong>From / To</strong> date range. The{" "}
              <strong>Recruiter</strong> and <strong>Job</strong> filters above
              do <strong>not</strong> apply here. To see recruiter-specific
              activity, use the <em>Recruiter Job Activity</em> table.
            </span>
          </div>
          <Row className="g-2 mt-1 manager-dashboard-row manager-work-row">
            <Col xl={4}>
              <Card className="manager-panel manager-panel-table manager-candidate-panel">
                <div className="manager-panel-header">
                  <div>
                    <div className="manager-panel-kicker">
                      Recent Candidate Activity
                    </div>
                    <h2 className="manager-panel-title">
                      Latest candidate updates
                    </h2>
                  </div>
                </div>
                <div id="recentAactivityTable">
                  <DataTable
                    value={recentCandidateActivity}
                    loading={loadingRecentCandidates}
                    responsiveLayout="scroll"
                    rows={8}
                    scrollable
                    scrollHeight="360px"
                  >
                    <Column
                      header="Candidate Name"
                      body={row => (
                        <span className="manager-table-strong">{row.name}</span>
                      )}
                    />
                    <Column field="role" header="Job Applied For" />
                    <Column
                      header="Current Stage"
                      body={row => (
                        <span
                          className={`manager-badge manager-badge-${row.stage
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {row.stage}
                        </span>
                      )}
                    />
                    <Column field="addedBy" header="Added By" />
                    <Column
                      header="Date Added"
                      body={row => formatDate(row.dateAdded)}
                    />
                    {/* <Column
                    header="Last Updated"
                    body={row => formatDate(row.lastUpdated)}
                  /> */}
                  </DataTable>
                </div>
              </Card>
            </Col>
            <Col xl={4}>
              <Card className="manager-panel manager-panel-table">
                <div className="manager-panel-header">
                  <div className="manager-panel-kicker">
                    Applications by Role
                  </div>
                </div>

                {loadingApplicationsByRole ? (
                  <div>Loading...</div>
                ) : (
                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      width: "100%",
                    }}
                  >
                    {applicationsByRole.map(job => (
                      <div
                        onClick={() => handleJobClick(job)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px",
                          borderBottom: "1px solid #eee",
                          cursor: "pointer",
                          width: "100%",
                        }}
                      >
                        {/* Job Title */}
                        <span style={{ fontWeight: 500, color: "#0d6efd" }}>
                          {job.job_title}
                        </span>

                        {/* Candidate Count with Label */}
                        <span
                          style={{
                            fontWeight: 600,
                            background: "#f1f5ff",
                            padding: "4px 10px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            color: "#333",
                            minWidth: "70px",
                            textAlign: "center",
                          }}
                        >
                          {job.total_candidates} Candidates
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="manager-panel manager-panel-table">
                <div className="manager-panel-header">
                  <div>
                    <div className="manager-panel-kicker">
                      Upcoming Interviews
                    </div>
                    <h2 className="manager-panel-title">
                      Today and next scheduled meetings
                    </h2>
                  </div>
                </div>

                <DataTable
                  value={upcomingInterviews}
                  loading={loadingInterviews}
                  responsiveLayout="scroll"
                  emptyMessage="No upcoming interviews for the day."
                  className="manager-table"
                  scrollable
                  scrollHeight="360px"
                >
                  <Column field="candidateName" header="Candidate Name" />
                  <Column field="position" header="Position" />
                  <Column field="interviewTime" header="Interview Time" />
                  <Column field="interviewer" header="Interviewer" />
                  <Column field="meetingType" header="Meeting Type" />
                </DataTable>
              </Card>
            </Col>
            {/* <Col xl={3}>
              <Card className="manager-panel manager-panel-table manager-team-panel">
                <div className="manager-panel-header">
                  <div>
                    <div className="manager-panel-kicker">Hiring Team Activity</div>
                    <h2 className="manager-panel-title">Recruiter workload</h2>
                  </div>
                </div>
                <div className="manager-team-list">
                  {recruiterActivity.map(recruiter => (
                    <div className="manager-team-row" key={recruiter.recruiter}>
                      <div>
                        <div className="manager-table-strong">{recruiter.recruiter}</div>
                        <div className="manager-table-muted">{recruiter.handled} candidates handled</div>
                        {recruiter.latestCandidate && (
                          <div className="manager-table-muted">Latest: {recruiter.latestCandidate}</div>
                        )}
                      </div>
                      <div className="manager-team-metrics">
                        <span>{recruiter.interviewsScheduled} interviews</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </Col> */}
          </Row>

          <Row className="g-2 mt-1 manager-dashboard-row">
            {/* <Col xl={6}>
              <Card className="manager-panel manager-panel-table manager-role-panel">
                <div className="manager-panel-header">
                  <div>
                    <div className="manager-panel-kicker">Open Roles</div>
                    <h2 className="manager-panel-title">Jobs needing hiring attention</h2>
                  </div>
                </div>
                <DataTable
                  value={jobActivity}
                  responsiveLayout="scroll"
                  scrollable
                  scrollHeight="330px"
                  emptyMessage="No open roles found for the selected filters."
                  className="manager-table"
                >
                  <Column field="role" header="Role" body={row => <span className="manager-table-strong">{row.role}</span>} />
                  <Column field="status" header="Status" />
                  <Column field="openings" header="Openings" />
                  <Column field="hiringManager" header="Recruiter" />
                  <Column header="Deadline" body={row => formatDate(row.deadline)} />
                </DataTable>
              </Card>
            </Col> */}
          </Row>

          <Row className="g-2 mt-1 manager-dashboard-row"></Row>
        </div>
      </Container>
      <Dialog
        header={`${selectedPipelineStage} Candidates (${selectedPipelineCandidates.length})`}
        visible={showPipelineModal}
        style={{ width: "75vw" }}
        onHide={() => {
          setShowPipelineModal(false)
          setSelectedPipelineStage("")
        }}
        maximizable
      >
        <DataTable
          value={selectedPipelineCandidates}
          responsiveLayout="scroll"
          scrollable
          scrollHeight="400px"
          emptyMessage={`No candidates found in ${selectedPipelineStage}.`}
          className="manager-table"
        >
          <Column
            field="name"
            header="Candidate"
            body={row => (
              <span className="manager-table-strong">{row.name}</span>
            )}
          />
          <Column field="role" header="Job Applied For" />
          <Column field="addedBy" header="Added By" />
          <Column header="Date Added" body={row => formatDate(row.dateAdded)} />
          {/* <Column
            header="Last Updated"
            body={row => formatDate(row.lastUpdated)}
          /> */}
          <Column field="status" header="Status" />
        </DataTable>
      </Dialog>
      <Dialog
        header={`Jobs handled by ${selectedRecruiterName} (${recruiterJobs.length})`}
        visible={showRecruiterModal}
        style={{ width: "70vw" }}
        onHide={() => {
          setShowRecruiterModal(false)
          setSelectedRecruiterName("")
          setRecruiterJobs([])
        }}
        maximizable
      >
        <DataTable
          value={recruiterJobs}
          responsiveLayout="scroll"
          scrollable
          scrollHeight="400px"
          loading={loadingRecruiterJobs}
          emptyMessage="No jobs found for this recruiter."
          className="manager-table"
        >
          <Column
            field="jobTitle"
            header="Job"
            body={row => (
              <span className="manager-table-strong">{row.jobTitle}</span>
            )}
          />
          <Column field="jobNumber" header="Job ID" />
          <Column field="openings" header="Openings" />
          <Column field="candidates" header="Candidates" />
          <Column field="status" header="Status" />
          <Column field="recruiter" header="Recruiter" />
        </DataTable>
      </Dialog>
      <Dialog
        header={cardDialogHeader}
        visible={showCardModal}
        style={{ width: "75vw" }}
        onHide={() => {
          setShowCardModal(false)
          setSelectedCardType("")
        }}
        maximizable
      >
        <div className="manager-card-detail-table-wrap">
          {cardData.length === 0 ? (
            <div className="manager-card-detail-empty">No data found.</div>
          ) : (
            <table className="manager-card-detail-table">
              <thead>
                <tr>
                  {cardDialogColumns.map(column => (
                    <th key={column.field}>{column.header}</th>
                  ))}
                  {console.info("Rendering card dialog with columns:", cardDialogColumns)}
                </tr>
              </thead>
              <tbody>
                {cardData.map((row, rowIndex) => (
                  <tr key={row.jobCandidateId || row.candidateId || row.jobNumber || rowIndex}>
                    {cardDialogColumns.map(column => (
                      <td key={column.field}>{renderCardCell(row, column)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Dialog>
      <Dialog
        visible={showJobDialog}
        onHide={() => setShowJobDialog(false)}
        header={selectedJobTitle}
        style={{ width: "600px" }}
      >
        {selectedJobStages.length === 0 ? (
          <div>No stage data found</div>
        ) : (
          selectedJobStages.map(stage => (
            <div
              key={stage.stage_id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 0",
                borderBottom: "1px solid #eee",
              }}
            >
              <span>{stage.stage_name}</span>
             <strong
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "999px", 
                  padding: "2px 8px",
                  background: "#f8fafc",
                  fontSize: "12px",
                  display: "inline-block",
                  minWidth: "24px",
                  textAlign: "center"
                }}
              >
                {stage.count}
              </strong>
            </div>
          ))
        )}
      </Dialog>
    </div>
  )
}

export default ManagerDashboard


